import { useEffect, useState } from 'react';
import { devMockAvailable, devMockCount } from '../../../lib/dev-mock-data';
import { devToolsUnlocked } from '../../../lib/dev-tools';
import { overriddenToggleCount } from '../../../lib/dev-feature-toggles';
import { isAppBannerForced, setAppBannerForced } from '../../../lib/dev-app-banner';
import { isHomeEmptyForced, setHomeEmptyForced } from '../../../lib/dev-home-empty-states';
import { getCreditsWidgetSim } from '../../../lib/credits-widget-sim';
import { getBlackFridaySim } from '../../../lib/black-friday-sim';
import { DevMockDataPanel } from './DevMockDataPanel';
import { DevFeatureTogglesPanel } from './DevFeatureTogglesPanel';
import { DevDemoAccountsPanel } from './DevDemoAccountsPanel';
import { DevDebugInfoPanel } from './DevDebugInfoPanel';
import { DevCreditsWidgetPanel } from './DevCreditsWidgetPanel';
import { DevBlackFridayPanel } from './DevBlackFridayPanel';
import { DevOutreachPreviewPanel } from './DevOutreachPreviewPanel';
import { DevSocialPostPreviewPanel } from './DevSocialPostPreviewPanel';
import { DevEmailCampaignPreviewPanel } from './DevEmailCampaignPreviewPanel';
import { DevBlogDraftPreviewPanel } from './DevBlogDraftPreviewPanel';
import { DevPictureArrivalPanel } from './DevPictureArrivalPanel';
import { useDevPictureArrivalAutoplay } from '../../../lib/dev-picture-arrival';
import styles from './DevMenu.module.css';

// The brand-mark dev menu — long-press (or right-click) the Symphony logo at the top of the sidebar.
// A dev-only escape hatch for the things a design needs but the product has no UI for:
//
//  - Reset animations — clears the one-time animation flags in localStorage (the piled-up cascade,
//    the brief reveal) and reloads so they replay. Web mirror of the native Dev Tools action.
//  - Mock data — fake how many of each entity this browser sees (lib/dev-mock-data.ts).
//  - Feature toggles — force a conducted flag On/Off in this browser, the web port of the native Dev
//    Tools ▸ Feature Toggles screen (lib/dev-feature-toggles.ts).
//  - Demo accounts — hop into a preset demo account, web port of the native switcher over the same
//    committed list (lib/dev-demo-accounts.ts). The SWITCH itself only works on localhost (the dev
//    proxy performs it), but the item is shown wherever the tools are: on a deployed origin the panel
//    says the control plane isn't mounted here, which is more use than a menu that hides an item.
//  - Share debug info — the web port of the native Dev Tools ▸ Share debug info row: email,
//    metaSiteId, active conversation id, and the derived Symphony Live monitoring URL, each with its
//    own Copy plus a Copy all (native hands the same text to the OS share sheet instead).
//
// The rows are `role="menuitem"`, not DS buttons: a menu item is a different control with its own
// roles and keyboard behaviour (same call as ComposerPill/AttachMenu).

export function DevMenu({ open, onClose, zIndex }: { open: boolean; onClose: () => void; zIndex: number }) {
  const [mockPanelOpen, setMockPanelOpen] = useState(false);
  const [demoPanelOpen, setDemoPanelOpen] = useState(false);
  const [togglesPanelOpen, setTogglesPanelOpen] = useState(false);
  const [debugInfoPanelOpen, setDebugInfoPanelOpen] = useState(false);
  const [creditsPanelOpen, setCreditsPanelOpen] = useState(false);
  const [blackFridayPanelOpen, setBlackFridayPanelOpen] = useState(false);
  const [blackFridaySimActive, setBlackFridaySimActive] = useState(false);
  const [outreachPanelOpen, setOutreachPanelOpen] = useState(false);
  const [socialPostPanelOpen, setSocialPostPanelOpen] = useState(false);
  const [emailCampaignPanelOpen, setEmailCampaignPanelOpen] = useState(false);
  const [blogDraftPanelOpen, setBlogDraftPanelOpen] = useState(false);
  const [pictureArrivalPanelOpen, setPictureArrivalPanelOpen] = useState(false);
  useDevPictureArrivalAutoplay();
  // Read once per opening — the counts only change via the panels, which reload the page.
  const [mockedCount, setMockedCount] = useState(0);
  const [forcedToggles, setForcedToggles] = useState(0);
  const [bannerForced, setBannerForced] = useState(false);
  const [homeEmptyForced, setHomeEmptyForcedState] = useState(false);
  const [creditsSimActive, setCreditsSimActive] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMockedCount(devMockCount());
    setForcedToggles(overriddenToggleCount());
    setBannerForced(isAppBannerForced());
    setHomeEmptyForcedState(isHomeEmptyForced());
    setCreditsSimActive(getCreditsWidgetSim() !== null);
    setBlackFridaySimActive(getBlackFridaySim() !== null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const resetAnimations = () => {
    try {
      const isAnimationFlag = /piledup|cascade|brief-|reveal/i;
      Object.keys(window.localStorage)
        .filter((k) => isAnimationFlag.test(k))
        .forEach((k) => window.localStorage.removeItem(k));
    } catch {
      /* storage unavailable */
    }
    window.location.reload();
  };

  return (
    <>
      {open && (
        <>
          {/* click-catcher to dismiss */}
          <div onClick={onClose} className={styles.catcher} style={{ zIndex }} />
          <div role="menu" className={styles.menu} style={{ zIndex: zIndex + 1 }}>
            <div className={styles.heading}>Dev</div>
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => {
                onClose();
                resetAnimations();
              }}
            >
              Reset animations
            </button>
            {/* Second gate, behind BrandHeader's: the item and the panel below check the runtime
                grant themselves, so neither opens from a call site that forgets to. */}
            {devMockAvailable() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  onClose();
                  setMockPanelOpen(true);
                }}
              >
                Mock data
                {mockedCount > 0 && <span className={styles.badge}>{mockedCount}</span>}
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  onClose();
                  setTogglesPanelOpen(true);
                }}
              >
                Feature toggles
                {forcedToggles > 0 && <span className={styles.badge}>{forcedToggles}</span>}
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  onClose();
                  setDemoPanelOpen(true);
                }}
              >
                Demo accounts
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  onClose();
                  setCreditsPanelOpen(true);
                }}
              >
                Credits widgets
                {creditsSimActive && <span className={styles.badge}>on</span>}
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                className={styles.item}
                onClick={() => {
                  onClose();
                  setBlackFridayPanelOpen(true);
                }}
              >
                Black Friday
                {blackFridaySimActive && <span className={styles.badge}>on</span>}
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  onClose();
                  setOutreachPanelOpen(true);
                }}
              >
                Outreach preview cards
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  onClose();
                  setSocialPostPanelOpen(true);
                }}
              >
                Social post preview cards
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  onClose();
                  setEmailCampaignPanelOpen(true);
                }}
              >
                Email campaign preview card
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  onClose();
                  setBlogDraftPanelOpen(true);
                }}
              >
                Blog draft preview card
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  onClose();
                  setPictureArrivalPanelOpen(true);
                }}
              >
                Picture arrival
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  setAppBannerForced(!bannerForced);
                  onClose();
                  window.location.reload();
                }}
              >
                {bannerForced ? 'Hide app banner' : 'Force app banner'}
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  setHomeEmptyForced(!homeEmptyForced);
                  onClose();
                  window.location.reload();
                }}
              >
                {homeEmptyForced ? 'Stop forcing home empty states' : 'Force home empty states'}
              </button>
            )}
            {devToolsUnlocked() && (
              <button
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  onClose();
                  setDebugInfoPanelOpen(true);
                }}
              >
                Share debug info
              </button>
            )}
          </div>
        </>
      )}
      {devMockAvailable() && <DevMockDataPanel open={mockPanelOpen} onClose={() => setMockPanelOpen(false)} />}
      {devToolsUnlocked() && <DevFeatureTogglesPanel open={togglesPanelOpen} onClose={() => setTogglesPanelOpen(false)} />}
      {devToolsUnlocked() && <DevDemoAccountsPanel open={demoPanelOpen} onClose={() => setDemoPanelOpen(false)} />}
      {devToolsUnlocked() && <DevDebugInfoPanel open={debugInfoPanelOpen} onClose={() => setDebugInfoPanelOpen(false)} />}
      {devToolsUnlocked() && <DevCreditsWidgetPanel open={creditsPanelOpen} onClose={() => setCreditsPanelOpen(false)} />}
      {devToolsUnlocked() && <DevBlackFridayPanel open={blackFridayPanelOpen} onClose={() => setBlackFridayPanelOpen(false)} />}
      {devToolsUnlocked() && <DevOutreachPreviewPanel open={outreachPanelOpen} onClose={() => setOutreachPanelOpen(false)} />}
      {devToolsUnlocked() && <DevSocialPostPreviewPanel open={socialPostPanelOpen} onClose={() => setSocialPostPanelOpen(false)} />}
      {devToolsUnlocked() && <DevEmailCampaignPreviewPanel open={emailCampaignPanelOpen} onClose={() => setEmailCampaignPanelOpen(false)} />}
      {devToolsUnlocked() && <DevBlogDraftPreviewPanel open={blogDraftPanelOpen} onClose={() => setBlogDraftPanelOpen(false)} />}
      {devToolsUnlocked() && <DevPictureArrivalPanel open={pictureArrivalPanelOpen} onClose={() => setPictureArrivalPanelOpen(false)} />}
    </>
  );
}
